/**
 * Shared Coveo Headless facet controllers.
 *
 * Controllers are created as singletons keyed by field name so that
 * both the horizontal filters and the FiltersDrawer share the same
 * state. The URL manager syncs engine state with the URL hash, so
 * selections made in either UI are reflected everywhere.
 */
import {
  DateFacet,
  Facet,
  NumericFacet,
  SearchEngine,
  buildDateFacet,
  buildDateRange,
  buildFacet,
  buildNumericFacet,
  buildNumericRange,
} from '@coveo/atomic-react';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';

const DAY_MS = 24 * 60 * 60 * 1000;

// Per-engine controller caches — keyed by engine instance so search and
// listing engines each get their own independent set of controllers.
type ControllerCache = {
  price: NumericFacet | null;
  rating: NumericFacet | null;
  publisher: Facet | null;
  releaseDate: DateFacet | null;
  category: Facet | null;
  sale: Facet | null;
};

const cacheByEngine = new WeakMap<object, ControllerCache>();

function getCache(eng: SearchEngine): ControllerCache {
  if (!cacheByEngine.has(eng)) {
    cacheByEngine.set(eng, {
      price: null,
      rating: null,
      publisher: null,
      releaseDate: null,
      category: null,
      sale: null,
    });
  }
  return cacheByEngine.get(eng)!;
}

// `bundleKey` matches the Coveo `ec_price_filter` facet value keys in the
// Contentful "Search UI Values" set (search-ui-values.ec_price_filter.*),
// resolved via microcopySetToResourceBundles — the same direct-lookup pattern
// the Values set was designed for and that SaleFacet/ReleaseDate use. The
// dollar-amount ranges are locale-agnostic, so only Free/Under/Over strictly
// need translating; `label` is the English fallback when no entry exists.
export const PRICE_RANGES = [
  { start: 0, end: 0, label: 'Free', bundleKey: 'free' },
  { start: 0.01, end: 20, label: 'Under $20', bundleKey: 'under-20' },
  { start: 20, end: 50, label: '$20 – $50', bundleKey: '20-50' },
  { start: 50, end: 100, label: '$50 – $100', bundleKey: '50-100' },
  { start: 100, end: 200, label: '$100 – $200', bundleKey: '100-200' },
  { start: 200, end: 999999, label: 'Over $200', bundleKey: 'over-200' },
];

export const RATING_RANGES = [
  { start: 5, end: 5, label: '5 Stars', bundleKey: '5-stars' },
  { start: 4, end: 5, label: '4+ Stars', bundleKey: '4-stars' },
  { start: 3, end: 5, label: '3+ Stars', bundleKey: '3-stars' },
  { start: 2, end: 5, label: '2+ Stars', bundleKey: '2-stars' },
  { start: 1, end: 5, label: '1+ Stars', bundleKey: '1-star' },
];

// Release Date facet ranges (relative to now)
export const DATE_RANGE_KEYS = [
  { days: 1, label: 'Last 24 hours', bundleKey: '1-day-ago' },
  { days: 7, label: 'Last week', bundleKey: '1-week-ago' },
  { days: 31, label: 'Last month', bundleKey: '1-month-ago' },
  { days: 180, label: 'Last 6 months', bundleKey: '6-months-ago' },
  { days: 365, label: 'Last year', bundleKey: '1-year-ago' },
];

export const SALE_LABEL_FALLBACKS: Record<string, string> = {
  flash_deal: 'Flash deal',
  on_sale: 'On sale now',
  new_release_discount: 'New release discount',
  quick_start_bundle: 'Quick start bundle',
  on_sale_soon: 'On sale soon',
};

export const buildSaleBundle = (
  searchValuesMicrocopy: MicrocopyItem[] | undefined
): Record<string, string> | undefined =>
  microcopySetToResourceBundles(searchValuesMicrocopy ?? []).ec_sale_filters;

export const resolveSaleLabel = (
  bundle: Record<string, string> | undefined,
  value: string
): string => bundle?.[value] ?? SALE_LABEL_FALLBACKS[value] ?? value;

export const buildPriceBundle = (
  searchValuesMicrocopy: MicrocopyItem[] | undefined
): Record<string, string> | undefined =>
  microcopySetToResourceBundles(searchValuesMicrocopy ?? []).ec_price_filter;

export const resolvePriceLabel = (
  bundle: Record<string, string> | undefined,
  bundleKey: string,
  fallback: string
): string => bundle?.[bundleKey] ?? fallback;

export function getPriceController(eng: SearchEngine): NumericFacet {
  const cache = getCache(eng);
  if (!cache.price) {
    cache.price = buildNumericFacet(eng, {
      options: {
        field: 'ec_price_filter',
        generateAutomaticRanges: false,
        currentValues: PRICE_RANGES.map((range) =>
          buildNumericRange({
            start: range.start,
            end: range.end,
            endInclusive: range.start === range.end || range.end === 999999,
          })
        ),
      },
    });
  }
  return cache.price;
}

export function getRatingController(eng: SearchEngine): NumericFacet {
  const cache = getCache(eng);
  if (!cache.rating) {
    cache.rating = buildNumericFacet(eng, {
      options: {
        field: 'ec_rating',
        generateAutomaticRanges: false,
        currentValues: RATING_RANGES.map((range) =>
          buildNumericRange({
            start: range.start,
            end: range.end,
            endInclusive: true,
          })
        ),
      },
    });
  }
  return cache.rating;
}

export function getPublisherController(eng: SearchEngine): Facet {
  const cache = getCache(eng);
  if (!cache.publisher) {
    cache.publisher = buildFacet(eng, {
      options: {
        field: 'publisher_name',
        numberOfValues: 20,
        sortCriteria: 'occurrences',
      },
    });
  }
  return cache.publisher;
}

export function getReleaseDateController(eng: SearchEngine): DateFacet {
  const cache = getCache(eng);
  if (!cache.releaseDate) {
    const now = new Date();
    const toApi = (d: Date) => d.toISOString().split('.')[0] + 'Z';

    cache.releaseDate = buildDateFacet(eng, {
      options: {
        field: 'first_published_at',
        generateAutomaticRanges: false,
        currentValues: DATE_RANGE_KEYS.map((range) =>
          buildDateRange({
            start: toApi(new Date(now.getTime() - range.days * DAY_MS)),
            end: toApi(now),
            endInclusive: true,
          })
        ),
      },
    });
  }
  return cache.releaseDate;
}

export function getCategoryController(eng: SearchEngine): Facet {
  const cache = getCache(eng);
  if (!cache.category) {
    cache.category = buildFacet(eng, {
      options: {
        field: 'ec_category',
        numberOfValues: 150,
        sortCriteria: 'alphanumeric',
      },
    });
  }
  return cache.category;
}

export function getSaleController(eng: SearchEngine): Facet {
  const cache = getCache(eng);
  if (!cache.sale) {
    cache.sale = buildFacet(eng, {
      options: {
        field: 'ec_sale_filters',
        numberOfValues: 10,
      },
    });
  }
  return cache.sale;
}

/**
 * Look up a human-readable label for a numeric range breadcrumb.
 * Searches PRICE_RANGES and RATING_RANGES.
 */
export function getNumericRangeLabel(start: number, end: number): string {
  const allRanges = [...PRICE_RANGES, ...RATING_RANGES];
  const match = allRanges.find((r) => r.start === start && r.end === end);
  return match?.label ?? `${start} – ${end}`;
}

/**
 * Look up a human-readable label for a date range breadcrumb.
 * Matches against DATE_RANGE_KEYS by comparing the duration between start and end.
 */
export function getDateRangeLabel(start: string, end: string): string {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const days = Math.round((endMs - startMs) / DAY_MS);
  const match = DATE_RANGE_KEYS.find((r) => r.days === days);
  return match?.label ?? `${start} – ${end}`;
}

/**
 * Reset all cached controllers for a given engine instance.
 * Call this when the engine is re-initialized (e.g., on auth change).
 */
export function resetFacetControllers(eng: SearchEngine): void {
  cacheByEngine.delete(eng);
}
